import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import api from '../lib/api';

export type { AppSetting } from '@kabbi/shared/schemas/responses';
import type { AppSetting } from '@kabbi/shared/schemas/responses';

interface SettingsState {
  settings: AppSetting[];
  loaded: boolean;
  fetchSettings: () => Promise<void>;
  updateSetting: (key: string, value: string) => Promise<void>;
  getSetting: (key: string) => AppSetting | undefined;
  getNumber: (key: string, fallback: number) => number;
  getString: (key: string, fallback: string) => string;
}

// Persist to localStorage so settings survive page reloads. App.tsx only
// triggers fetchSettings() after the user is authenticated — without
// persistence the reload would either show stale-empty state until the
// round-trip completes, or trigger a network call on every navigation.
export const useSettingsStore = create<SettingsState>()(
  persist(
    (set, get) => ({
      settings: [],
      loaded: false,

      fetchSettings: async () => {
        try {
          const { data } = await api.get('/settings');
          // Coerce to [] so downstream `.find()` / `.map()` calls never see
          // undefined — covers the case where a misconfigured API base URL
          // makes nginx serve index.html (HTML body) at the request path.
          set({
            settings: Array.isArray(data?.settings) ? data.settings : [],
            loaded: true,
          });
        } catch {
          set({ loaded: true });
        }
      },

      updateSetting: async (key, value) => {
        // Optimistic update — apply immediately so UI (scheduler, color pickers) reacts at once
        const previous = get().settings.find((s) => s.key === key);
        set((state) => ({
          settings: state.settings.map((s) => (s.key === key ? { ...s, value } : s)),
        }));
        try {
          const { data } = await api.patch(`/settings/${key}`, { value });
          // Confirm with server response (normalizes value, updatedAt, etc.)
          set((state) => ({
            settings: state.settings.map((s) => (s.key === key ? data.setting : s)),
          }));
        } catch (err: unknown) {
          // Revert optimistic update so store stays in sync with DB
          if (previous) {
            set((state) => ({
              settings: state.settings.map((s) => (s.key === key ? previous : s)),
            }));
          }
          const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
          throw new Error(msg || 'Failed to save');
        }
      },

      getSetting: (key) => get().settings.find((s) => s.key === key),

      getNumber: (key, fallback) => {
        const s = get().settings.find((s) => s.key === key);
        if (!s) return fallback;
        const n = Number(s.value);
        return isNaN(n) ? fallback : n;
      },

      getString: (key, fallback) => {
        const s = get().settings.find((s) => s.key === key);
        return s ? s.value : fallback;
      },
    }),
    {
      name: 'kabbi-settings',
      storage: createJSONStorage(() => localStorage),
      // Only persist the settings array itself, not the `loaded` flag —
      // we want every fresh page load to revalidate from the API after
      // login, even if there's a cached array to render in the meantime.
      partialize: (state) =>
        ({ settings: state.settings }) as Partial<SettingsState>,
    },
  ),
);
